/**
 * Hierarchy Search — Lab `hierarchy-search` for Strix.
 *
 * Tag usernames or member UUIDs (Enter / pick suggestion) → chain table by viewer role.
 * Eye → Member 360 · List → Member List filtered to that name.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import {
  DEFAULT_PAGE_SIZE,
  Banner,
  Button,
  Card,
  DataTable,
  Icon,
  PageHead,
  Pager,
  Spinner,
  StateBlock,
  type Column,
} from '@w7/ui'
import { useSession } from '@/app/queries'
import { useSearchHierarchyNew } from '@/api/hooks/member/useSearchHierarchyNew'
import { useSearchHierarchyPage } from '@/api/hooks/member/useSearchHierarchyPage'
import {
  canUseHierarchySearch,
  demoSuggestUsernames,
  hierarchyColumnsForRole,
  hierarchySearchLive,
  isHierarchySearchMemberId,
  type HierarchySearchRow,
} from '@/api/map/hierarchySearch'
import type { Role } from '@/data/types'
import './hierarchy-search.css'

const PAGE_SIZE = DEFAULT_PAGE_SIZE
const DEBOUNCE_MS = 100

export function HierarchySearchPage() {
  const { data: session, isLoading, isError } = useSession()
  const role = session?.account.role

  // Wait for session — redirecting while role is still undefined sends a refresh to Member List.
  if (isLoading || (!session && !isError)) {
    return (
      <div className="w7-auth w7-auth-center">
        <Spinner size={22} />
      </div>
    )
  }

  if (!canUseHierarchySearch(role)) {
    return <Navigate to="/downline" replace />
  }

  return <HierarchySearchBody role={role!} />
}

function HierarchySearchBody({ role }: { role: Role }) {
  const columnsMeta = hierarchyColumnsForRole(role)
  const [tags, setTags] = useState<string[]>([])
  const [draft, setDraft] = useState('')
  const [debouncedDraft, setDebouncedDraft] = useState('')
  const [page, setPage] = useState(1)
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const rootRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const live = hierarchySearchLive()
  const searchById = tags.length === 1 && isHierarchySearchMemberId(tags[0]!)

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedDraft(draft), DEBOUNCE_MS)
    return () => window.clearTimeout(t)
  }, [draft])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open])

  const suggest = useSearchHierarchyNew(debouncedDraft, open && live && !isHierarchySearchMemberId(draft), {
    prefetchOnEmpty: true,
  })

  const search = useSearchHierarchyPage(tags, {
    page,
    serchById: searchById,
    enabled: tags.length > 0,
  })

  const suggestions = useMemo(() => {
    const liveNames = suggest.data ?? []
    const names = (live && liveNames.length
      ? liveNames
      : demoSuggestUsernames(draft, tags)
    ).filter((n) => !tags.includes(n))
    const needle = draft.trim().toLowerCase()
    if (!needle || !live || !liveNames.length) return names.slice(0, 12)
    return names.filter((n) => n.toLowerCase().includes(needle)).slice(0, 12)
  }, [suggest.data, tags, draft, live])

  useEffect(() => {
    setActiveIndex(0)
  }, [suggestions, draft])

  const addTag = (name: string) => {
    const clean = name.trim()
    if (!clean) return
    if (isHierarchySearchMemberId(clean)) {
      setTags([clean])
    } else {
      setTags((prev) => {
        const namesOnly = prev.filter((t) => !isHierarchySearchMemberId(t))
        return namesOnly.includes(clean) ? namesOnly : [...namesOnly, clean]
      })
    }
    setDraft('')
    setPage(1)
    setOpen(false)
    inputRef.current?.focus()
  }

  const removeTag = (name: string) => {
    setTags((prev) => prev.filter((t) => t !== name))
    setPage(1)
  }

  const columns = useMemo<Column<HierarchySearchRow>[]>(() => {
    const cols: Column<HierarchySearchRow>[] = [
      {
        id: 'index',
        header: '#',
        cell: (_row, index) => (
          <span className="w7-hier-index">{(page - 1) * PAGE_SIZE + index + 1}</span>
        ),
      },
      ...columnsMeta.map((meta) => ({
        id: meta.id,
        header: meta.header,
        cell: (row: HierarchySearchRow) => {
          const name = row[meta.id]
          const idKey = `${meta.id}Id` as keyof HierarchySearchRow
          const id = row[idKey] as string | undefined
          if (!name) return <span className="w7-hier-empty">—</span>
          return (
            <HierarchyNameCell
              name={name}
              id={id}
              showListAction={meta.id !== 'Player'}
            />
          )
        },
      })),
      {
        id: 'rowId',
        header: 'ID',
        cell: (row) => <HierarchyIdCell id={row._id} />,
      },
    ]
    return cols
  }, [columnsMeta, page])

  const rows = search.data?.rows ?? []
  const total = search.data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const showSuggest =
    open &&
    !isHierarchySearchMemberId(draft) &&
    (suggestions.length > 0 ||
      (live && suggest.isFetching) ||
      (live && suggest.isError) ||
      draft.trim().length > 0)

  return (
    <div className="w7-hier-page">
      <PageHead
        title="Hierarchy Search"
        subtitle="Find anyone beneath you and see the full upline chain in one row."
      />

      {!live ? (
        <Banner tone="info">
          Demo suggestions — sign in against hierarchy-service for live downline search.
        </Banner>
      ) : null}

      <Card className="w7-hier-card" flush>
        <div className="w7-hier-search" ref={rootRef}>
          <label className="w7-hier-field" htmlFor="w7-hier-input">
            <Icon name="search" size={16} />
            <div className="w7-hier-chips">
              {tags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  className="w7-hier-chip"
                  onClick={() => removeTag(tag)}
                  title={`Remove ${tag}`}
                >
                  <span>{tag}</span>
                  <span aria-hidden="true">×</span>
                </button>
              ))}
              <input
                id="w7-hier-input"
                ref={inputRef}
                className="w7-hier-input"
                type="text"
                value={draft}
                autoComplete="off"
                spellCheck={false}
                placeholder={
                  tags.length
                    ? 'Add another name or id'
                    : 'Enter member name or id'
                }
                aria-expanded={showSuggest}
                aria-controls="w7-hier-suggest-list"
                aria-autocomplete="list"
                aria-activedescendant={
                  showSuggest && suggestions[activeIndex]
                    ? `w7-hier-opt-${activeIndex}`
                    : undefined
                }
                onChange={(e) => {
                  setDraft(e.target.value)
                  if (!isHierarchySearchMemberId(e.target.value)) setOpen(true)
                  else setOpen(false)
                }}
                onFocus={() => {
                  if (!isHierarchySearchMemberId(draft)) setOpen(true)
                }}
                onKeyDown={(e) => {
                  if (isHierarchySearchMemberId(draft)) {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      if (draft.trim()) addTag(draft)
                    } else if (e.key === 'Backspace' && !draft && tags.length) {
                      removeTag(tags[tags.length - 1]!)
                    }
                    return
                  }
                  if (e.key === 'ArrowDown') {
                    e.preventDefault()
                    setOpen(true)
                    setActiveIndex((i) => Math.min(i + 1, Math.max(suggestions.length - 1, 0)))
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault()
                    setActiveIndex((i) => Math.max(i - 1, 0))
                  } else if (e.key === 'Enter') {
                    e.preventDefault()
                    const pick = suggestions[activeIndex]
                    if (open && pick) addTag(pick)
                    else {
                      // Only accept names already in downline suggestions — free-text
                      // would otherwise hit the API for foreign trees when closure is stale.
                      const needle = draft.trim().toLowerCase()
                      const exact = suggestions.find((s) => s.toLowerCase() === needle)
                      if (exact) addTag(exact)
                    }
                  } else if (e.key === 'Backspace' && !draft && tags.length) {
                    removeTag(tags[tags.length - 1]!)
                  } else if (e.key === 'Escape') {
                    setOpen(false)
                  }
                }}
              />
            </div>
            {tags.length ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setTags([])
                  setPage(1)
                  inputRef.current?.focus()
                }}
              >
                Clear
              </Button>
            ) : null}
          </label>

          {showSuggest ? (
            <ul
              id="w7-hier-suggest-list"
              className="w7-hier-suggest"
              role="listbox"
              aria-label="Member suggestions"
            >
              {live && suggest.isFetching && suggestions.length === 0 ? (
                <li className="w7-hier-suggest-status" role="presentation">
                  <Spinner size={14} />
                  <span>Searching downline…</span>
                </li>
              ) : null}
              {live && suggest.isError ? (
                <li className="w7-hier-suggest-status" role="presentation">
                  Could not load suggestions — hierarchy API unreachable.
                </li>
              ) : null}
              {suggestions.map((name, index) => (
                <li key={name} role="presentation">
                  <button
                    type="button"
                    id={`w7-hier-opt-${index}`}
                    role="option"
                    aria-selected={index === activeIndex}
                    className="w7-hier-suggest-option"
                    data-active={index === activeIndex ? 'true' : undefined}
                    onMouseEnter={() => setActiveIndex(index)}
                    onMouseDown={(e) => {
                      e.preventDefault()
                      addTag(name)
                    }}
                  >
                    <Icon name="users" size={14} />
                    <HighlightMatch text={name} needle={draft} />
                  </button>
                </li>
              ))}
              {!suggest.isFetching &&
              !suggest.isError &&
              suggestions.length === 0 &&
              draft.trim() ? (
                <li className="w7-hier-suggest-status" role="presentation">
                  No match for “{draft.trim()}” in your downline.
                </li>
              ) : null}
            </ul>
          ) : null}
        </div>

        <p className="w7-hier-hint">
          {tags.length
            ? searchById
              ? '1 id selected · match only in your downline (other trees stay hidden)'
              : `${tags.length} selected · exact match in your downline`
            : 'Type a name or paste a member UUID, press Enter — results show the chain above each hit (your downline only).'}
        </p>
      </Card>

      {!tags.length ? (
        <StateBlock icon="search" title="Search your downline">
          Start typing a username or paste a member id. Results open as a Player → Master → SM → DM
          chain for your level.
        </StateBlock>
      ) : search.isLoading ? (
        <StateBlock icon="search" title="Searching…">
          Looking up the hierarchy chain for each username.
        </StateBlock>
      ) : search.isError ? (
        <StateBlock icon="error" title="Search failed" tone="error">
          Hierarchy API did not return results. Check the connection and try Clear, then search
          again.
        </StateBlock>
      ) : (
        <Card
          title={total ? `${total} match${total === 1 ? '' : 'es'}` : undefined}
          flush
        >
          <DataTable
            label="Hierarchy search results"
            columns={columns}
            rows={rows}
            rowKey={(r) => r._id}
            loading={search.isFetching && rows.length === 0}
            empty={
              <StateBlock icon="search" title="No matches">
                Those names are not in your downline, or the spelling does not match exactly.
              </StateBlock>
            }
          />
          <Pager
            page={page}
            pageCount={pageCount}
            onChange={setPage}
            total={total}
            pageSize={PAGE_SIZE}
          />
        </Card>
      )}
    </div>
  )
}

function HierarchyNameCell({
  name,
  id,
  showListAction,
}: {
  name: string
  id?: string
  showListAction: boolean
}) {
  if (!id) return <span className="w7-hier-name">{name}</span>
  return (
    <span className="w7-hier-cell">
      <Link to={`/member/${id}`} className="w7-hier-link" title="Open member">
        {name}
      </Link>
      <span className="w7-hier-cell-actions">
        <Link to={`/member/${id}`} className="w7-hier-icon-link" title="Open member" aria-label={`Open ${name}`}>
          <Icon name="eye" size={14} />
        </Link>
        {showListAction ? (
          <Link
            to={`/downline?q=${encodeURIComponent(name)}`}
            className="w7-hier-icon-link"
            title="Open in Member List"
            aria-label={`Member List for ${name}`}
          >
            <Icon name="downline" size={14} />
          </Link>
        ) : null}
      </span>
    </span>
  )
}

function HierarchyIdCell({ id }: { id: string }) {
  return (
    <span className="w7-hier-id">
      <span>{id}</span>
      <button
        type="button"
        className="w7-hier-icon-btn"
        title="Copy id"
        aria-label="Copy id"
        onClick={() => {
          void navigator.clipboard?.writeText(id)
        }}
      >
        <Icon name="copy" size={14} />
      </button>
    </span>
  )
}

function HighlightMatch({ text, needle }: { text: string; needle: string }) {
  const q = needle.trim()
  if (!q) return <span>{text}</span>
  const idx = text.toLowerCase().indexOf(q.toLowerCase())
  if (idx < 0) return <span>{text}</span>
  return (
    <span>
      {text.slice(0, idx)}
      <mark className="w7-hier-mark">{text.slice(idx, idx + q.length)}</mark>
      {text.slice(idx + q.length)}
    </span>
  )
}
